import { withApollo } from 'react-apollo'
import { withRouter } from 'react-router-dom'

import { Type, read, withAnalyticsClient, GenericAnalyticsClient } from '@acorns/web-utils'
import { withToggles, type WithTogglesProps } from '@acorns/web-toggles-react'
import * as Sentry from '@sentry/browser'
import { merge } from 'ramda'
import {
  branch,
  compose,
  fromRenderProps,
  renderComponent,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import { APP_ID, APP_VERSION, IS_RESUME_FLOW } from 'app-constants'
import { ActionType, PageName } from 'generated/types'
import { isAuthenticated, setAuthItems } from 'utils/auth'
import { withBackend } from 'utils/backend'
import { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import removeDuplicateQueryParams from 'utils/sourceAttributes'
import { ResumeFlowContext, ResumeFlowContextProps } from '../../../providers/ResumeFlowProvider'

import ResumeFlow from './ResumeFlow'
import userQuery from './user.query.gql'
import { didLogin, isNewTab } from './utils'
import { UserContext, UserContextProps } from '../../../providers/UserProvider'

const unauthenticated = (ctx) => {
  return merge(ctx, { unauthenticated: true })
}

const fetchRefreshCookie =
  () =>
  (ctx = {}) => {
    if (isAuthenticated()) {
      const token = read(Type.token)
      return Promise.resolve(merge(ctx, { refreshCookie: token }))
    }
    return Promise.resolve(unauthenticated(ctx))
  }

const fetchUser =
  ({ client, setRegistrationComplete, registrationFunnel }) =>
  (ctx: any = {}) => {
    if (ctx.unauthenticated) {
      return ctx
    }

    return client
      .query({ query: userQuery, fetchPolicy: 'network-only' })
      .then(({ data: { user } }) => {
        let previousFlow

        if (!user) {
          return unauthenticated(ctx)
        }

        registrationFunnel(user.uuid)

        setRegistrationComplete(user.registrationComplete)

        return merge(ctx, {
          previousFlow,
          regComplete: user.registrationComplete,
          userUuid: user.uuid,
          userEmail: user.email,
        })
      })
  }

const handleResumeCases =
  (props) =>
  (ctx: any = {}) => {
    if (ctx.unauthenticated) {
      return props.handleSkipResume()
    }

    if (ctx.regComplete) {
      return props.handleRegistrationComplete()
    }

    if (window.location.pathname !== FlowRoute.Success) {
      return props.handleResume(ctx)
    }

    props.setLoading(false)
  }

const handleSkipResume =
  ({ setLoading, setIsResumeFlow }) =>
  () => {
    setResumeFlow(false, setIsResumeFlow)
    setLoading(false)
  }

const handleRegistrationComplete =
  ({ env, setIsResumeFlow }) =>
  () => {
    setResumeFlow(false, setIsResumeFlow)
    window.location.assign(env.get('webAppUrl'))
  }

const handleResume =
  ({ setShowWelcomeModal, backend, setFlow, setUserData, setIsResumeFlow }) =>
  ({ refreshCookie, userUuid, userEmail, previousFlow }) => {
    const params = removeDuplicateQueryParams(location.search)

    const flowToResume = params.flow || previousFlow

    const resume = () => {
      setAuthItems({
        identityId: userUuid,
        token: refreshCookie,
      })

      if (userEmail) {
        setUserData({
          email: userEmail,
        })
      }

      backend.defaults.headers.Authorization = `token ${refreshCookie}`
      setResumeFlow(true, setIsResumeFlow)
      setFlow(flowToResume)

      window.analytics.identify(userUuid, {
        app_version: APP_VERSION,
        funnel_type: flowToResume,
        onboarding_app_id: APP_ID,
      })

      Sentry.configureScope((scope) => {
        scope.setUser({ id: userUuid })
      })
      setShowWelcomeModal(true)
    }

    return resume()
  }

const handleError =
  ({ setLoading, flow, setIsResumeFlow }) =>
  (err) => {
    setResumeFlow(false, setIsResumeFlow)
    sendToSentry(err, {
      breadcrumb: `Attempting to resume ${flow} flow`,
    })
    setLoading(false)
  }

const handleContinue =
  ({ route, isDynamicFunnelEnabled, routeToPage }) =>
  () => {
    if (isDynamicFunnelEnabled) {
      return routeToPage(PageName.SIGNUP, ActionType.CONTINUE)
    }

    route({ from: FlowRoute.Signup, action: 'continue' })
  }

type HandlerProps = {
  fetchRefreshCookie: () => any
  fetchUser: () => any
  handleError: () => any
  handleRegistrationComplete: () => any
  handleResume: (ctx: any) => any
  handleSkipResume: () => any
  handleContinue: () => void
  handleResumeCases: (ctx: any) => void
  setIsRegistrationComplete: () => void
}

export type WithResumeFlowProps = {
  newTab: boolean
  didLogin: boolean
  loading: boolean
  setLoading: (loading: boolean) => void
  showWelcomeModal: boolean
  setShowWelcomeModal: (showWelcomeModal: boolean) => void
  isRegistrationFlow: boolean
  registrationComplete: boolean
  analytics: GenericAnalyticsClient
} & HandlerProps &
  WithTogglesProps

// setIsResumeFlow is used to update the state of the ResumeFlowProvider
// @TODO - eventually we should move all resume flow logic to the ResumeFlowProvider
const setResumeFlow = (isResuming = false, setIsResumeFlow?: (value: boolean) => void) => {
  window.sessionStorage.setItem(IS_RESUME_FLOW, isResuming.toString().toLowerCase())
  if (setIsResumeFlow) {
    setIsResumeFlow(isResuming)
  }
}

export default compose(
  fromRenderProps(
    FunnelContext.Consumer,
    ({
      registrationFunnel,
      metadata,
      isDynamicFunnelEnabled,
      routeToPage,
    }: FunnelContextProps) => ({
      registrationFunnel,
      metadata,
      isDynamicFunnelEnabled,
      routeToPage,
    }),
  ),
  fromRenderProps(UserContext.Consumer, ({ setUserData }: UserContextProps) => ({
    setUserData,
  })),
  fromRenderProps(ResumeFlowContext.Consumer, ({ setIsResumeFlow }: ResumeFlowContextProps) => ({
    setIsResumeFlow,
  })),
  withEnv,
  withState('loading', 'setLoading', true),
  withBackend,
  withApollo,
  withFlowRouter,
  withRouter,
  withToggles,
  withAnalyticsClient,
  withState('loading', 'setLoading', true),
  withState('showWelcomeModal', 'setShowWelcomeModal', false),
  withState('registrationComplete', 'setRegistrationComplete', false),
  withProps(({ env, isRegistrationFlow }) => {
    return {
      didLogin: didLogin(document.referrer, env),
      newTab: isNewTab(),
      isRegistrationFlow,
    }
  }),
  withHandlers<{}, {}>({
    fetchRefreshCookie,
    fetchUser,
    handleError,
    handleRegistrationComplete,
    handleResume,
    handleSkipResume,
    handleContinue,
  }),
  withHandlers<{}, {}>({
    handleResumeCases,
  }),
  branch(({ loading }) => loading, renderComponent(ResumeFlow)),
)

export const isResumeFlow = (): boolean => window.sessionStorage.getItem(IS_RESUME_FLOW) === 'true'
